import './SubscriptionExpiryBanner.css';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, CalendarClock, X } from 'lucide-react';
import UserService from '../../Service/UserService';
import TokenService from '../../Service/TokenService';
import PaymentService from '../../Service/PaymentService';
import { getPaidExpiry, formatExpiryDate, formatDaysLeft } from '../../utils/subscription';

const DISMISS_KEY = 'subscriptionExpiryBannerDismissed';
const TARIFFS_PATH = '/settings/tariffs';
// Space the banner takes at the bottom of the screen (its height + a gap).
// Toasts share the bottom-right corner and read it to stack above the banner.
const OFFSET_VAR = '--sub-expiry-banner-offset';
const OFFSET_GAP = 8;

// Salon cabinet routes only — never on public pages (/book, /c/:token,
// /landing, legal pages) even if the owner happens to be logged in.
const CABINET_PREFIXES = ['/calendar', '/clients', '/agreements', '/settings', '/sms'];

function isCabinetPath(pathname) {
    return CABINET_PREFIXES.some(p => pathname === p || pathname.startsWith(`${p}/`));
}

function readDismissed() {
    try {
        return sessionStorage.getItem(DISMISS_KEY);
    } catch {
        // storage unavailable (private mode, blocked storage) — banner just shows again
        return null;
    }
}

/**
 * Soft reminder that a paid tariff (Pro/Business) is about to expire — the
 * backend rolls the company back to Free after `expires_at`. Mounted once in
 * App next to the PWA banners; the subscription is fetched once per logged-in
 * user (not per page), and re-fetched only when a different user logs in.
 * Dismissal lasts for the browser session and is keyed by `expires_at`, so a
 * renewed (or different) subscription can surface it again.
 *
 * During the grace period (`in_grace`) the banner turns red and its dismissal
 * is in-memory only: it comes back on the next page reload, since the company
 * is about to be downgraded to Free and its masters deactivated.
 */
function SubscriptionExpiryBanner() {
    const location = useLocation();
    const navigate = useNavigate();
    const [expiry, setExpiry] = useState(null);
    const [expiresKey, setExpiresKey] = useState(null);
    const [dismissed, setDismissed] = useState(readDismissed);
    const [graceDismissed, setGraceDismissed] = useState(false);
    const fetchedForRef = useRef(null);

    const authenticated = UserService.isAuthenticated();

    useEffect(() => {
        if (!authenticated) {
            fetchedForRef.current = null;
            setExpiry(null);
            return;
        }
        if (!isCabinetPath(location.pathname)) return;
        const userId = TokenService.getUserId() ?? 'anon';
        if (fetchedForRef.current === userId) return;
        fetchedForRef.current = userId;

        // No cleanup-cancel here: a route change mid-request must not drop the
        // result (the ref already marks this user as fetched). Only a user
        // switch/logout in the meantime invalidates it.
        PaymentService.getSubscription(navigate).then(data => {
            if (fetchedForRef.current !== userId) return;
            const paid = getPaidExpiry(data);
            setExpiry(paid);
            setExpiresKey(paid ? String(data.expires_at) : null);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authenticated, location.pathname]);

    const handleDismiss = () => {
        if (expiry?.inGrace) {
            // Grace: hide only until reload, not for the whole browser session.
            setGraceDismissed(true);
            return;
        }
        try {
            sessionStorage.setItem(DISMISS_KEY, expiresKey ?? '1');
        } catch {
            // ignore storage errors (private mode, blocked storage)
        }
        setDismissed(expiresKey ?? '1');
    };

    const visible = authenticated && !!expiry?.expiringSoon
        && isCabinetPath(location.pathname) && location.pathname !== TARIFFS_PATH
        && !(expiry.inGrace ? graceDismissed : (dismissed && dismissed === expiresKey));
    const bannerRef = useRef(null);

    useLayoutEffect(() => {
        const node = bannerRef.current;
        if (!visible || !node) return undefined;
        const root = document.documentElement;
        const update = () => root.style.setProperty(OFFSET_VAR, `${node.offsetHeight + OFFSET_GAP}px`);
        update();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
        observer?.observe(node);
        return () => {
            observer?.disconnect();
            root.style.removeProperty(OFFSET_VAR);
        };
    }, [visible, expiry?.inGrace]);

    if (!visible) return null;

    const tariffName = expiry.tariffName || 'платний';

    if (expiry.inGrace) {
        return (
            <div ref={bannerRef} className="sub-expiry-banner sub-expiry-banner--danger" role="alert">
                <AlertTriangle size={16} className="sub-expiry-banner__icon" />
                <span className="sub-expiry-banner__text">
                    <strong>Тариф {tariffName} закінчився.</strong>{' '}
                    Пільговий період до {formatExpiryDate(expiry.graceDate || expiry.expiresDate)}: потім
                    компанію буде переведено на Free, а майстрів, крім власника, деактивовано.
                </span>
                <Link to={TARIFFS_PATH} className="sub-expiry-banner__link">
                    Продовжити
                </Link>
                <button
                    type="button"
                    className="sub-expiry-banner__close"
                    onClick={handleDismiss}
                    aria-label="Закрити"
                >
                    <X size={14} />
                </button>
            </div>
        );
    }

    return (
        <div ref={bannerRef} className="sub-expiry-banner" role="status">
            <CalendarClock size={16} className="sub-expiry-banner__icon" />
            <span className="sub-expiry-banner__text">
                Тариф {tariffName} закінчується {formatDaysLeft(expiry.daysLeft)}
                {' '}<span className="sub-expiry-banner__date">({formatExpiryDate(expiry.expiresDate)})</span>
            </span>
            <Link to={TARIFFS_PATH} className="sub-expiry-banner__link">
                Продовжити
            </Link>
            <button
                type="button"
                className="sub-expiry-banner__close"
                onClick={handleDismiss}
                aria-label="Закрити"
            >
                <X size={14} />
            </button>
        </div>
    );
}

export default SubscriptionExpiryBanner;
